import {
  HiOutlineSquares2X2,
} from "react-icons/hi2";
import { IoSettingsOutline } from "react-icons/io5";
import { SlCalender } from "react-icons/sl";
import { FaRegCalendarAlt, FaUserMd, FaRunning } from "react-icons/fa";
import { CiUser } from "react-icons/ci";
import {
  MdOutlineReport,
  MdOutlineSubscriptions,
  MdOutlineAnalytics,
  MdChat,
  MdOutlineGroup,
  MdOutlinePeopleAlt,
  MdOutlinePayment,
  MdOutlineContacts,
  MdOutlineCampaign,
  MdOutlineTrackChanges,
  MdOutlineInventory2,
  MdOutlineListAlt,
  MdOutlineWarningAmber,
  MdOutlineSettings,
  MdOutlineReceiptLong,
  MdOutlineAccountBalanceWallet,
  MdOutlineBadge,
  MdOutlineGavel,
  MdOutlineLocalOffer,
  MdOutlineSmartToy,
  MdOutlineDirectionsCar,
  MdOutlineFactCheck,
  MdOutlineTune,
  MdOutlineMenuBook,
  MdOutlineAccountTree,
  MdOutlineWorkOutline,
  MdOutlineDateRange,
  MdOutlineAssessment,
  MdOutlineSyncAlt,
  MdOutlineDashboard,
  MdOutlineEventAvailable,
  MdOutlineRequestQuote,
  MdOutlineDriveEta,
  MdOutlineSwapHoriz,
  MdOutlineLocalShipping,
  MdOutlineAssignmentReturn,
  MdOutlineAccountBalance,
  MdOutlineSavings,
  MdOutlinePointOfSale,
  MdOutlineLocalAtm,
  MdOutlineShoppingCart,
  MdOutlineRequestPage,
  MdOutlineSettingsSuggest,
  MdOutlineBuildCircle,
  MdOutlineEngineering,
  MdOutlineVerifiedUser,
  MdOutlineInsights,
  MdOutlineCompareArrows,
  MdOutlineReceipt,
  MdOutlineBusiness,
  MdOutlineHandshake,
  MdOutlineNotificationsActive,
  MdOutlineSentimentSatisfied,
  MdOutlineCarRepair,
  MdOutlineSpeed,
  MdOutlineFeaturedPlayList,
  MdOutlineWarehouse,
  MdOutlineStorefront,
  MdOutlineEmojiEvents,
  MdOutlineChatBubbleOutline,
  MdOutlineMoveToInbox,
  MdOutlineAutorenew,
  MdOutlineShoppingBag,
} from "react-icons/md";
import { expenseBranchPath } from "../../utils/expenseBranchPath";
import { HR_PERMISSIONS, hasHrFeature, hasHrPermission } from "../../constants/hrPermissions";
import { DMS_PERMISSIONS, FIN_PERMISSIONS, PROC_PERMISSIONS, hasAnyModulePermission, hasModulePermission, isModuleAdmin, isModuleEnabled } from "../../constants/platformModules";

/**
 * Single source of truth for the desktop Sidebar's nav structure. Replaces
 * the three hand-duplicated JSX branches (isTeamUser / isSportsService /
 * default) that used to live inline in Sidebar.jsx - every label, icon and
 * visibility rule below was copied verbatim from those branches, not
 * redesigned, so per-industry behavior is unchanged except for the one
 * approved addition (Lead Management now also wired into the Sports branch,
 * still gated by canViewLeads exactly like the other branches).
 *
 * Returns an ordered array of entries:
 *   { type: "link",  key, to, label, icon }                      - no children (e.g. Dashboard)
 *   { type: "group", key, label, icon, items: [{key,to,label,icon}] } - collapsible group
 * Groups with zero resolved items are omitted entirely, so a branch that
 * doesn't have any Finance-type items (for example) simply never renders
 * a "Finance" header.
 */
export function getNavGroups(ctx) {
  const {
    isTeamUser,
    isCrewUser,
    // HR staff (role 7): the HR screens and nothing else.
    isHrUser = false,
    isClinicService,
    isSportsService,
    isCarService,
    isSalonService,
    canViewLeads,
    planId,
    reportsAvailable,
    bookingLabel = "Booking",
    // Active branches [{ id, name }] - each one gets its own Jobs menu item.
    branches = [],
    // GET hrms/access: { enabled, permissions, is_tenant_admin, is_employee, ... }
    hrAccess = null,
    // Booking workflows in use for this person (GET workflow_access): offers Tasks.
    workflowEnabled = false,
    // GET platform/access: which of Finance / DMS are on, and this person's permission sets.
    moduleAccess = null,
  } = ctx;

  const finEnabled = isModuleEnabled(moduleAccess, "FIN");
  const dmsEnabled = isModuleEnabled(moduleAccess, "DMS");
  const procEnabled = isModuleEnabled(moduleAccess, "PROC");
  const finUser = hasAnyModulePermission(moduleAccess, "FIN", FIN_PERMISSIONS);
  const dmsUser = hasAnyModulePermission(moduleAccess, "DMS", DMS_PERMISSIONS);
  const procUser = hasAnyModulePermission(moduleAccess, "PROC", PROC_PERMISSIONS);

  const hrEnabled = !!(hrAccess && hrAccess.enabled);
  // Plans without the HR module still get the Employees capability, for the owner and admins.
  const employeesOnly = !hrEnabled && !!(hrAccess && hrAccess.employees_enabled) && !isTeamUser && !isCrewUser;

  // HR staff get no scheduling, customers, finance or dashboard: just HR (or, on a plan without the HR module,
  // the Employees screen). Computed below once the HR items exist.

  const isPlanThree = planId === 3;

  const providerLabel = isTeamUser
    ? "Crew"
    : isSportsService
    ? "Coach"
    : isClinicService
    ? "Doctor"
    : isCarService || isSalonService
    ? "Service Provider"
    : "Crew";

  const customerLabel = isClinicService ? "Patients" : isSportsService ? "Players" : "Customers";

  const scheduling = [];
  // Crew persona: Calendar only (view + edit existing bookings - enforced
  // in Bookings.jsx, not here) - no New Booking/Estimations/Subscription.
  if (!isClinicService && !isCrewUser) {
    scheduling.push({ key: "new-booking", to: "/vendor_bookings", label: `New ${bookingLabel}`, icon: SlCalender });
  }
  // Calendar View: every branch has it except Sports (matches today's
  // sidebar, which never links /calendar for the Sports industry). Kept as
  // the 2nd item - its own gate (isSportsService) is independent of New
  // Booking's (isClinicService), so each item's visibility is pushed
  // separately rather than nested, to preserve both regardless of order.
  if (!isSportsService) {
    scheduling.push({ key: "calendar", to: "/calendar", label: "Calendar View", icon: FaRegCalendarAlt });
  }
  if (!isClinicService && !isCrewUser) {
    // Estimations feed into New Booking (Convert to Booking opens this same
    // screen pre-filled) - same visibility rule as New Booking itself.
    // "Service Quotations": priced service quotes that become bookings. Dealership (vehicle) quotations
    // are a separate document in the Dealership group.
    scheduling.push({ key: "estimations", to: "/estimations", label: "Service Quotations", icon: MdOutlineListAlt });
    // UtilStop "Bid Your Price" requests — customers' bids this pro was invited to
    // (accept / counter / decline). Only meaningful for pros linked to a UtilStop
    // vendor profile; the page itself explains when the account isn't linked.
    scheduling.push({ key: "bid-requests", to: "/bid_requests", label: "Bid Requests", icon: MdOutlineGavel });
  }
  // Subscription (recurring service plans customers enroll in) used to be
  // its own "Plans" group - moved in here since bookings directly reference
  // the enrollment via subscription_id, making Scheduling the group for
  // every way a piece of work gets initiated: ad-hoc (New Booking),
  // pre-priced (Estimations), recurring (Subscription).
  if (!isCrewUser) {
    scheduling.push({ key: "subscription", to: "/subscription", label: "Subscription", icon: MdOutlineSubscriptions });
  }

  // Steps of a booking's workflow (drop-off, cleaning, pick-up ...): crew see their own,
  // staff see anyone's. Offered only once workflows are in use.
  if (workflowEnabled) {
    scheduling.push({ key: "tasks", to: "/tasks", label: isCrewUser ? "My Tasks" : "Tasks", icon: MdOutlineListAlt });
  }

  const customers = [];
  // Crew persona doesn't get a Customers record link today.
  if (!isTeamUser && !isCrewUser) {
    customers.push({ key: "customers", to: "/customers", label: customerLabel, icon: MdOutlineContacts });
  }

  // Crew persona sees only their own profile inside the Crew tab (enforced
  // in Providers.jsx/ProviderProfile.jsx, not here). Providers stays even for HR-enabled tenants: it is the only
  // screen that edits booking-only data (fee, service, appointment duration, shifts) HR has no equivalent for.
  const workforce = [
    { key: "providers", to: "/providers", label: providerLabel, icon: isClinicService ? FaUserMd : isSportsService ? FaRunning : CiUser },
  ];
  if (employeesOnly) {
    workforce.push({ key: "employees", to: "/hr/team", label: "Employees", icon: MdOutlineGroup });
  }
  // Where the payroll add-on is on, the real screens live in the HR group below.
  const hrPayroll = hrEnabled && hasHrFeature(hrAccess, "PAYROLL");

  // HR module (Core HR + Leave): only for tenants on the HR plan. What each person
  // sees follows their permission sets; the API enforces the same rules again.
  const hr = [];
  if (hrEnabled) {
    const isHr = hasHrPermission(hrAccess, HR_PERMISSIONS.HR_ADMIN, HR_PERMISSIONS.HR_VIEWER);
    const isHrAdmin = hasHrPermission(hrAccess, HR_PERMISSIONS.HR_ADMIN);
    if (isHr) hr.push({ key: "hr-overview", to: "/hr", label: "HR Overview", icon: HiOutlineSquares2X2 });
    if (hrAccess.is_employee) hr.push({ key: "hr-my-space", to: "/hr/my", label: "My Space", icon: CiUser });
    hr.push({ key: "hr-approvals", to: "/hr/approvals", label: "Approvals", icon: MdOutlineListAlt });
    hr.push({ key: "hr-leave", to: "/hr/leave", label: "Leave", icon: FaRegCalendarAlt });
    hr.push({ key: "hr-attendance", to: "/hr/attendance", label: "Attendance", icon: FaRunning });
    hr.push({ key: "hr-roster", to: "/hr/roster", label: "Roster", icon: MdOutlineTrackChanges });
    hr.push({ key: "hr-employees", to: "/hr/employees", label: isHr ? "Employees" : "My Team", icon: MdOutlineGroup });
    hr.push({ key: "hr-documents", to: "/hr/documents", label: "Documents", icon: MdOutlineReceiptLong });
    hr.push({ key: "hr-org-chart", to: "/hr/org-chart", label: "Org Chart", icon: MdOutlinePeopleAlt });
    if (hrPayroll && hasHrPermission(hrAccess, HR_PERMISSIONS.PAYROLL_ADMIN, HR_PERMISSIONS.FINANCE_APPROVER)) hr.push({ key: "hr-payroll", to: "/hr/payroll", label: "Payroll", icon: MdOutlinePayment });
    if (hrPayroll && hrAccess.is_employee) hr.push({ key: "hr-payslips", to: "/hr/payslips", label: "My Payslips", icon: MdOutlineReceiptLong });
    if (isHrAdmin) hr.push({ key: "hr-settings", to: "/hr/settings", label: "HR Settings", icon: MdOutlineSettings });
  }

  if (isHrUser) {
    const items = hrEnabled
      ? hr
      : hrAccess && hrAccess.employees_enabled
      ? [{ key: "employees", to: "/hr/team", label: "Employees", icon: MdOutlineGroup }]
      : [];
    return items.length ? [{ type: "group", key: "hr", label: "HR", icon: MdOutlineGroup, items }] : [];
  }

  // Inventory: the same plan and role rule as Growth below - plan_id === 3, every
  // persona except Crew (Super Admin, Admin, Vendor and Support). Like Growth,
  // the plan check is nav-only; App.js's /inventory/* ProtectedRoute enforces the
  // role part ["1","2","3","6"] for direct URLs.
  // The API opens every inventory read to any signed-in role and keeps all
  // writes at role 3, so Support gets a read-only module (the item screens hide
  // their write controls for anyone but role 3). Settings is the exception:
  // it is nothing but write controls (costing method, markup, categories), so
  // it stays with Admin/Vendor, like Admin Setup.
  // The single old "/inventory" link is expanded into its real sub-pages now
  // that the Inventory module has 5 of them.
  // A dealer manages its stock here too (one place for parts and stock), whatever its plan. So does a buyer
  // with Procurement: awarded RFQs become purchase orders here.
  // Warehouses and purchasing add their pages when Inventory Settings switches them on.
  const inventory = [];
  const invFlags = (moduleAccess && moduleAccess.inventory) || {};
  if (!isCrewUser && (isPlanThree || isModuleEnabled(moduleAccess, "DMS") || procEnabled)) {
    inventory.push(
      { key: "inventory-items", to: "/inventory", label: "Items", icon: MdOutlineInventory2 },
      { key: "inventory-stock", to: "/inventory/stock", label: "Stock", icon: MdOutlineWarehouse },
      { key: "inventory-low-stock", to: "/inventory/low-stock", label: "Low Stock Alerts", icon: MdOutlineWarningAmber }
    );
    if (invFlags.multi_warehouse) {
      inventory.push(
        { key: "inventory-transfers", to: "/inventory/transfers", label: "Transfers", icon: MdOutlineCompareArrows },
        { key: "inventory-counts", to: "/inventory/counts", label: "Stock Takes", icon: MdOutlineFactCheck }
      );
    }
    if (invFlags.purchasing_enabled) {
      inventory.push(
        { key: "inventory-suppliers", to: "/inventory/suppliers", label: "Suppliers", icon: MdOutlineStorefront },
        { key: "inventory-po", to: "/inventory/purchase-orders", label: "Purchase Orders", icon: MdOutlineShoppingCart },
        { key: "inventory-grn", to: "/inventory/receipts", label: "Goods Receipts", icon: MdOutlineMoveToInbox },
        { key: "inventory-reorder", to: "/inventory/reorder", label: "Reorder", icon: MdOutlineAutorenew }
      );
    }
    inventory.push(
      { key: "inventory-reports", to: "/inventory/reports", label: "Reports", icon: MdOutlineListAlt },
      { key: "inventory-analytics", to: "/inventory/analytics", label: "Analytics", icon: MdOutlineAnalytics }
    );
    if (!isTeamUser) {
      inventory.push({ key: "inventory-settings", to: "/inventory/settings", label: "Settings", icon: IoSettingsOutline });
    }
  }

  // Growth: Crew persona never gets it, regardless of plan - enforced here
  // for the nav (hidden item) and again at the route level in App.js
  // (Growth routes split into their own ProtectedRoute excluding role 5)
  // for strict URL-level enforcement. Support (isTeamUser) follows the
  // exact same plan-tier + Sports-exclusion rule as Admin/Vendor - it used
  // to have its own ungated branch here, which let Support see Growth in
  // cases (Sports industry, non-plan-3) where Admin/Vendor could not.
  const growth = [];
  if (!isCrewUser && !isSportsService && isPlanThree) {
    growth.push({ key: "channels", to: "/channel", label: "Channels", icon: MdChat });
    // The AI Assistant that answers customers in Channels. Settings are for the vendor and admins only (App.js
    // and the API enforce the same); Support still sees the assistant's drafts inside Channels.
    if (!isTeamUser) growth.push({ key: "ai-assistant", to: "/ai-assistant", label: "AI Assistant", icon: MdOutlineSmartToy });
    growth.push({ key: "campaigns", to: "/campaigns", label: "Campaigns", icon: MdOutlineCampaign });
    // Moved here from the Customers group - previously gated by canViewLeads
    // (vendor 320 only); now available to every vendor/industry under the
    // same rule as the rest of Growth. The page itself (LeadsList.jsx)
    // handles the data difference: the UtilStop support account (320) still
    // sees leads auto-pulled from stalled UtilStop bookings, every other
    // vendor starts empty and creates leads manually - same underlying
    // ezp_leads/ezp_lead_followups tables and same update/WhatsApp flow for
    // everyone either way.
    growth.push({ key: "leads", to: "/leads", label: "Lead Management", icon: MdOutlineTrackChanges });
  }

  // Expense Management: car service only, and Admin/Vendor only (matches
  // App.js's /expense-management/* ProtectedRoute allowedRoles=["1","2","3"];
  // the API also re-checks role and industry on every request).
  const expenseManagement = [];
  if (isCarService && !isTeamUser && !isCrewUser) {
    // One Jobs menu per active branch (the branch is fixed by the menu, so
    // the Jobs page itself has no branch dropdown), then Setup.
    branches.forEach((branch) =>
      expenseManagement.push({
        key: `expense-management-branch-${branch.id}`,
        to: expenseBranchPath(branch.id),
        label: branch.name,
        icon: MdOutlineReceiptLong,
      })
    );
    expenseManagement.push({ key: "expense-management-settings", to: "/expense-management/settings", label: "Setup", icon: IoSettingsOutline });
  }

  // Dealership (DMS): car service vendors the Super Admin switched it on for. Each item follows the person's DMS
  // permission sets; the API checks them again. Enquiries are the Leads screen: on plan 3 it is already in
  // Growth, so it is only repeated here for dealers on other plans.
  const dealership = [];
  if (dmsEnabled && dmsUser) {
    const dmsHas = (...p) => hasModulePermission(moduleAccess, "DMS", ...p);
    const seller = dmsHas("DMS_SALES", "DMS_SALES_MANAGER", "DMS_ADMIN");
    const manager = dmsHas("DMS_SALES_MANAGER", "DMS_ADMIN");
    if (seller) dealership.push({ key: "dms-dashboard", to: "/dms/dashboard", label: "Sales Dashboard", icon: MdOutlineDashboard });
    dealership.push({ key: "dms-availability", to: "/dms/availability", label: "Vehicle Availability", icon: MdOutlineEventAvailable });
    if (!isPlanThree && !isCrewUser) dealership.push({ key: "dms-enquiries", to: "/leads", label: "Enquiries", icon: MdOutlineTrackChanges });
    if (seller) {
      dealership.push(
        { key: "dms-quotes", to: "/dms/quotes", label: "Quotations", icon: MdOutlineRequestQuote },
        { key: "dms-orders", to: "/dms/orders", label: "Sales Orders", icon: MdOutlineReceiptLong },
        { key: "dms-test-drives", to: "/dms/test-drives", label: "Test Drives", icon: MdOutlineDriveEta },
        { key: "dms-trade-ins", to: "/dms/trade-ins", label: "Trade-ins", icon: MdOutlineSwapHoriz }
      );
    }
    if (dmsHas("DMS_SALES", "DMS_SALES_MANAGER", "DMS_ADMIN", "DMS_TECHNICIAN", "DMS_SERVICE_ADVISOR")) dealership.push({ key: "dms-pdi", to: "/dms/pdi", label: "PDI Requests", icon: MdOutlineFactCheck });
    dealership.push({ key: "dms-stock", to: "/dms/vehicles", label: "Vehicle Stock", icon: MdOutlineInventory2 });
    if (isModuleEnabled(moduleAccess, "DMS", "PARTS") && dmsHas("DMS_PARTS", "DMS_SALES_MANAGER", "DMS_ADMIN")) dealership.push({ key: "dms-parts", to: "/dms/parts", label: "Parts Sales", icon: MdOutlineSettingsSuggest });
    if (isModuleEnabled(moduleAccess, "DMS", "WORKSHOP")) {
      if (dmsHas("DMS_SERVICE_ADVISOR", "DMS_TECHNICIAN", "DMS_PARTS", "DMS_ADMIN")) dealership.push({ key: "dms-ro", to: "/dms/repair-orders", label: "Repair Orders", icon: MdOutlineBuildCircle });
      if (dmsHas("DMS_SERVICE_ADVISOR", "DMS_TECHNICIAN", "DMS_ADMIN")) dealership.push({ key: "dms-board", to: "/dms/technician-board", label: "Technician Board", icon: MdOutlineEngineering });
      if (dmsHas("DMS_SERVICE_ADVISOR", "DMS_ADMIN")) dealership.push({ key: "dms-warranty", to: "/dms/warranty-claims", label: "Warranty Claims", icon: MdOutlineVerifiedUser });
      if (dmsHas("DMS_SERVICE_ADVISOR", "DMS_ADMIN")) dealership.push({ key: "dms-bodyshop", to: "/dms/bodyshop", label: "Body Shop", icon: MdOutlineCarRepair });
      if (dmsHas("DMS_SERVICE_ADVISOR", "DMS_SALES_MANAGER", "DMS_ADMIN")) dealership.push({ key: "dms-as-dash", to: "/dms/aftersales-dashboard", label: "After-sales Dashboard", icon: MdOutlineSpeed });
    }
    if (seller) {
      dealership.push(
        { key: "dms-fi", to: "/dms/fi-deals", label: "F&I Deals", icon: MdOutlineHandshake },
        { key: "dms-fleet", to: "/dms/fleet", label: "Fleet Sales", icon: MdOutlineFeaturedPlayList }
      );
    }
    if (dmsHas("DMS_SERVICE_ADVISOR", "DMS_SALES", "DMS_SALES_MANAGER", "DMS_ADMIN")) {
      dealership.push(
        { key: "dms-recalls", to: "/dms/recalls", label: "Recalls", icon: MdOutlineCampaign },
        { key: "dms-reminders", to: "/dms/reminders", label: "Service Reminders", icon: MdOutlineNotificationsActive },
        { key: "dms-csi", to: "/dms/csi", label: "Customer Satisfaction", icon: MdOutlineSentimentSatisfied }
      );
    }
    if (dmsHas("DMS_CASHIER", "DMS_PARTS", "DMS_SALES", "DMS_SALES_MANAGER", "DMS_ADMIN")) dealership.push({ key: "dms-counter", to: "/dms/counter-sales", label: "Counter Sales", icon: MdOutlinePointOfSale });
    if (dmsHas("DMS_CASHIER", "DMS_SALES_MANAGER", "DMS_ADMIN") && !finUser) dealership.push({ key: "dms-pdc", to: "/fin/cash?tab=cheques", label: "PDC", icon: MdOutlineLocalAtm });
    if (dmsHas("DMS_ADMIN", "DMS_SALES_MANAGER", "DMS_PARTS")) dealership.push({ key: "dms-purchasing", to: "/dms/purchasing", label: "Purchasing", icon: MdOutlineShoppingCart });
    if (manager) {
      dealership.push(
        { key: "dms-transfers", to: "/dms/transfers", label: "Goods Transfers", icon: MdOutlineLocalShipping },
        { key: "dms-returns", to: "/dms/returns", label: "Return Orders", icon: MdOutlineAssignmentReturn }
      );
    }
    if (isModuleAdmin(moduleAccess, "DMS")) dealership.push({ key: "dms-setup", to: "/dms/setup", label: "Dealership Setup", icon: MdOutlineDirectionsCar });
  }

  // Procurement: the Super Admin switched it on for this vendor. The buyer-side screens follow the person's
  // PROC permission sets (the API checks them again). RFQs, quotations, awards and orders add their
  // items in the next phases.
  const procurement = [];
  if (procEnabled && procUser) {
    procurement.push({ key: "proc-dashboard", to: "/proc/dashboard", label: "Overview", icon: MdOutlineDashboard });
    procurement.push({ key: "proc-rfqs", to: "/proc/rfqs", label: "RFQs", icon: MdOutlineRequestQuote });
    procurement.push({ key: "proc-awards", to: "/proc/awards", label: "Awards", icon: MdOutlineEmojiEvents });
    procurement.push({ key: "proc-orders", to: "/proc/orders", label: "Orders", icon: MdOutlineLocalShipping });
    procurement.push({ key: "proc-messages", to: "/proc/messages", label: "Messages", icon: MdOutlineChatBubbleOutline });
    procurement.push({ key: "proc-suppliers", to: "/proc/suppliers", label: "Suppliers", icon: MdOutlineStorefront });
    if (isModuleAdmin(moduleAccess, "PROC")) procurement.push({ key: "proc-setup", to: "/proc/setup", label: "Procurement Setup", icon: MdOutlineTune });
  }

  const finance = [];
  if (!isTeamUser && !isCrewUser) {
    finance.unshift({ key: "payments", to: "/payments", label: "Payments", icon: MdOutlinePayment });
    // Company overhead costs (rent, utilities, office bills) - distinct from
    // Payments (customer receivables) and Branch Jobs (per-vehicle job
    // costing/invoicing). Gated the same as Payments so anyone who can see
    // the Finance menu sees this sub-menu too.
    finance.push({
      key: "opex",
      to: "/finance/operating-expenses",
      label: "Operating Expenses",
      icon: MdOutlineAccountBalanceWallet,
    });
  }

  // Finance module (GL / AR / AP ...): joins the Finance group above, for the people holding a Finance permission.
  if (finEnabled && finUser) {
    finance.push(
      { key: "fin-dashboard", to: "/fin/dashboard", label: "Finance Dashboard", icon: MdOutlineInsights },
      { key: "fin-receivables", to: "/fin/receivables", label: "Receivables", icon: MdOutlineAccountBalance },
      { key: "fin-cash", to: "/fin/cash", label: "Cash & Bank", icon: MdOutlineSavings },
      { key: "fin-payables", to: "/fin/payables", label: "Payables", icon: MdOutlineRequestPage },
      { key: "fin-bankrec", to: "/fin/bank-reconciliation", label: "Bank Reconciliation", icon: MdOutlineCompareArrows },
      { key: "fin-tax", to: "/fin/tax-returns", label: "Tax Returns", icon: MdOutlineReceipt },
      { key: "fin-assets", to: "/fin/fixed-assets", label: "Fixed Assets", icon: MdOutlineBusiness },
      { key: "fin-journals", to: "/fin/journals", label: "Journals", icon: MdOutlineMenuBook },
      { key: "fin-coa", to: "/fin/chart-of-accounts", label: "Chart of Accounts", icon: MdOutlineAccountTree },
      { key: "fin-projects", to: "/fin/projects", label: "Projects", icon: MdOutlineWorkOutline },
      { key: "fin-periods", to: "/fin/periods", label: "Periods", icon: MdOutlineDateRange },
      { key: "fin-reports", to: "/fin/reports", label: "Financial Reports", icon: MdOutlineAssessment }
    );
    const finAdmin = isModuleAdmin(moduleAccess, "FIN");
    if (finAdmin && isModuleEnabled(moduleAccess, "FIN", "ERP_CONNECTOR")) finance.push({ key: "fin-erp", to: "/fin/erp", label: "ERP Integration", icon: MdOutlineSyncAlt });
    if (finAdmin) finance.push({ key: "fin-setup", to: "/fin/setup", label: "Finance Setup", icon: MdOutlineTune });
  }

  // One approvals inbox (HR + Finance + Dealership + Procurement requests) for anyone who can raise or decide them there.
  const showApprovals = (finEnabled || dmsEnabled || procEnabled) && (finUser || dmsUser || procUser || Number(moduleAccess?.approvals?.pending) > 0);

  // Insights & Settings: Admin/Vendor only - Support (isTeamUser) and Crew
  // never get these, enforced again at the route level in App.js.
  const insights = [];
  if (!isTeamUser && !isCrewUser) {
    insights.push({ key: "analytics", to: "/analytics", label: "Analytics", icon: MdOutlineAnalytics });
    if (reportsAvailable) {
      insights.push({ key: "reports", to: "/reports", label: "Reports", icon: MdOutlineReport });
    }
  }

  const settings = (isTeamUser || isCrewUser) ? [] : [
    { key: "admin-setup", to: "/admin-setup", label: "Admin Setup", icon: MdOutlineSettings },
    // Icons, offers and coupons the vendor manages for their own business (vendor-only, like Admin Setup).
    { key: "branding", to: "/branding", label: "Branding & Offers", icon: MdOutlineLocalOffer },
  ];

  const groups = [
    { type: "link", key: "dashboard", to: "/dashboard", label: "Dashboard", icon: HiOutlineSquares2X2 },
    ...(showApprovals ? [{ type: "link", key: "approvals", to: "/approvals", label: "Approvals", icon: MdOutlineFactCheck }] : []),
    { type: "group", key: "scheduling", label: "Scheduling", icon: SlCalender, items: scheduling },
    { type: "group", key: "customers", label: "Customers", icon: MdOutlineContacts, items: customers },
    // "Human Resource" fits when this group still holds Employee too; once HR is enabled it holds only
    // Providers, right beside the real "HR" group, so it's relabeled (and given its own icon, distinct from
    // HR's people icon) to avoid two things both called HR.
    { type: "group", key: "workforce", label: hrEnabled ? "Providers" : "Human Resource", icon: hrEnabled ? MdOutlineBadge : MdOutlinePeopleAlt, items: workforce },
    { type: "group", key: "hr", label: "HR", icon: MdOutlineGroup, items: hr },
    { type: "group", key: "inventory", label: "Inventory", icon: MdOutlineInventory2, items: inventory },
    { type: "group", key: "dealership", label: "Dealership", icon: MdOutlineDirectionsCar, items: dealership },
    { type: "group", key: "procurement", label: "Procurement", icon: MdOutlineShoppingBag, items: procurement },
    { type: "group", key: "growth", label: "Growth", icon: MdOutlineCampaign, items: growth },
    { type: "group", key: "finance", label: "Finance", icon: MdOutlinePayment, items: finance },
    { type: "group", key: "expense-management", label: "Branch Jobs", icon: MdOutlineReceiptLong, items: expenseManagement },
    { type: "group", key: "insights", label: "Insights", icon: MdOutlineAnalytics, items: insights },
    { type: "group", key: "settings", label: "Settings", icon: MdOutlineSettings, items: settings },
  ];

  return groups.filter((g) => g.type === "link" || g.items.length > 0);
}

/**
 * Flat list of every destination in the nav, in menu order, each tagged with the
 * group it lives in. The phone bar and menu sheet read this so they can never
 * list something the desktop sidebar would not.
 */
export function flattenNav(navGroups) {
  const out = [];
  navGroups.forEach((entry) => {
    if (entry.type === "link") {
      out.push({ key: entry.key, to: entry.to, label: entry.label, icon: entry.icon, groupKey: null, groupLabel: null });
    } else {
      entry.items.forEach((item) =>
        out.push({ ...item, groupKey: entry.key, groupLabel: entry.label })
      );
    }
  });
  return out;
}

// What each persona reaches for all day, in priority order. A key only counts if
// the person can actually open it (it came out of getNavGroups for them); the
// bar is back-filled from menu order, so it always has up to `max` tabs.
const PRIMARY_PREFERENCE = {
  hr: ["hr-overview", "hr-approvals", "hr-attendance", "hr-leave", "employees"],
  crew: ["tasks", "calendar", "dashboard", "providers"],
  clinic: ["dashboard", "calendar", "customers", "providers"],
  sports: ["dashboard", "new-booking", "customers", "providers"],
  // Bid Requests holds the slot Calendar had (Calendar stays in the Menu).
  // Support has no Customers record link, so Crew (providers) takes the slot.
  default: ["dashboard", "new-booking", "bid-requests", "customers", "providers"],
};

export function getPrimaryTabs(navGroups, persona = {}, max = 4) {
  const { isHrUser, isCrewUser, isClinicService, isSportsService } = persona;
  const preference = isHrUser
    ? PRIMARY_PREFERENCE.hr
    : isCrewUser
    ? PRIMARY_PREFERENCE.crew
    : isClinicService
    ? PRIMARY_PREFERENCE.clinic
    : isSportsService
    ? PRIMARY_PREFERENCE.sports
    : PRIMARY_PREFERENCE.default;

  const all = flattenNav(navGroups);
  const picked = [];
  preference.forEach((key) => {
    const found = all.find((item) => item.key === key);
    if (found && !picked.includes(found)) picked.push(found);
  });
  all.forEach((item) => {
    if (picked.length < max && !picked.includes(item)) picked.push(item);
  });
  return picked.slice(0, max);
}
